import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { flushSync } from 'react-dom';
import { formatDisplayDateTime } from '../../utils/dateTimeFormat';
import { getWellView } from './rtuCluster';

/**
 * Desktop column sizing shared by the flat list and the grouped list, keyed by the stable field name. The tables use
 * `table-layout: fixed` with explicit widths, so cell content never sets a width by itself:
 *  - a column's minimum is its content width (the longest of its header and its values, see `useContentWidths`), never
 *    below its floor `min` here; a drag may widen a column and can never take it below its minimum;
 *  - when the columns do not all fit the container each column is exactly its content width and the table scrolls
 *    sideways; when they fit with room to spare the spare space is shared out in proportion to the content widths
 *    (see `resolveColumnWidths`).
 */
export interface ColumnSizing {
  min: number;
}

export const SELECTION_COLUMN_WIDTH = 48;
export const ACTIONS_COLUMN_WIDTH = 148;

const TIMESTAMP: ColumnSizing = { min: 150 };

export const COLUMN_SIZING: Record<string, ColumnSizing> = {
  status: { min: 72 },
  name: { min: 160 },
  serialNo: { min: 120 },
  wellName: { min: 140 },
  wellId: { min: 80 },
  location: { min: 100 },
  configVersion: { min: 110 },
  deploymentVersion: { min: 110 },
  diskUsage: { min: 80 },
  cpuUsage: { min: 80 },
  statusObservedAtUtc: TIMESTAMP,
  lastFileReceivedAtUtc: TIMESTAMP,
  // optional columns the column picker can switch on
  lastData: TIMESTAMP,
  installDate: TIMESTAMP,
  lastConfigUpdate: TIMESTAMP,
  reportingToServer: TIMESTAMP,
  publicIPAddress: { min: 100 },
  modemIP: { min: 100 },
  ethernetIP: { min: 100 },
  groupId: { min: 70 },
  field: { min: 100 },
  // grouped view: the first (producer / group / expander) column
  provider: { min: 140 },
};

const FALLBACK_SIZING: ColumnSizing = { min: 80 };

export const sizingFor = (field: string): ColumnSizing => COLUMN_SIZING[field] ?? FALLBACK_SIZING;

/** Content widths by field, as measured by `useContentWidths`. */
export type ContentWidths = Record<string, number>;

/** The narrowest a column may be: its floor, or its longest value when that is wider. */
export const columnMinPx = (field: string, content: ContentWidths = {}): number =>
  Math.max(sizingFor(field).min, content[field] ?? 0);

/** The table border and sub-pixel rounding: a table filling its container stays this much narrower, so it never scrolls. */
export const TABLE_FILL_SLACK_PX = 2;

export interface ResolvedColumnWidths {
  widths: Record<string, number>;
  /** Width of the whole table, including the fixed selection / actions columns. */
  total: number;
}

/**
 * Pure width algorithm for one table.
 *  - minimum of a column = max(floor, content width); a manual (dragged) width is kept when it is wider;
 *  - if the columns (plus `fixedPx` for selection / actions) fill or overflow the container: every column is exactly
 *    its minimum / manual width and the table is as wide as their sum;
 *  - if they fit with room to spare: the spare space is shared out among the columns that were not dragged, in
 *    proportion to their minimum, so the table fills the container and no column is below its minimum.
 * `containerWidth` 0 (unknown) never stretches.
 */
export const resolveColumnWidths = (input: {
  fields: string[];
  content: ContentWidths;
  manual: Record<string, number>;
  containerWidth: number;
  fixedPx: number;
}): ResolvedColumnWidths => {
  const { fields, content, manual, containerWidth, fixedPx } = input;
  const mins = fields.map((field) => columnMinPx(field, content));
  const widths: Record<string, number> = {};
  fields.forEach((field, index) => {
    widths[field] = Math.max(mins[index], manual[field] ?? 0);
  });
  const baseTotal = fields.reduce((total, field) => total + widths[field], fixedPx);
  const target = containerWidth - TABLE_FILL_SLACK_PX;
  if (containerWidth <= 0 || baseTotal >= target) {
    return { widths, total: baseTotal };
  }
  const growable = fields.filter((field) => manual[field] === undefined);
  const growBasis = growable.reduce((total, field) => total + columnMinPx(field, content), 0);
  if (growable.length === 0 || growBasis <= 0) {
    return { widths, total: baseTotal };
  }
  const spare = target - baseTotal;
  let given = 0;
  growable.forEach((field, index) => {
    const share =
      index === growable.length - 1 ? spare - given : Math.floor((spare * columnMinPx(field, content)) / growBasis);
    widths[field] += share;
    given += share;
  });
  return { widths, total: target };
};

export const widthStyle = (field: string, widths: Record<string, number>, content: ContentWidths = {}) => ({
  width: `${widths[field] ?? columnMinPx(field, content)}px`,
  minWidth: `${columnMinPx(field, content)}px`,
});

/** Serial no.: a long serial shows its first 6 and last 8 characters (serials share long prefixes), 15 characters at most. */
export const SERIAL_HEAD_CHARS = 6;
export const SERIAL_TAIL_CHARS = 8;

/** "700100573632...3a39d7c5" style: first 6 + an ellipsis + last 8; a value of 15 characters or fewer is shown in full. */
export const middleEllipsis = (value: string, head = SERIAL_HEAD_CHARS, tail = SERIAL_TAIL_CHARS): string =>
  value.length <= head + tail + 1 ? value : `${value.slice(0, head)}\u2026${value.slice(-tail)}`;

/** The Producer column (grouped view, desktop) is never wider than this by default; a longer label ends in an ellipsis. */
export const PRODUCER_MAX_WIDTH_PX = 240;

/** Upper bound of the content width of a column (its content width is the smaller of its longest value and this). */
export const CONTENT_WIDTH_CAPS: Record<string, number> = { provider: PRODUCER_MAX_WIDTH_PX };

// ---- Content measuring ------------------------------------------------------------------------------------------

/** Cell padding (8px each side), the 0.8px gridlines and a little sub-pixel safety. */
export const CELL_PADDING_PX = 20;
/** Header: the 4px gap and the 13px sort icon next to the label. */
export const HEADER_EXTRA_PX = 17;
/** Chevron slot (24px) every row of a cluster layout reserves in the RTU name cell. */
export const NAME_CHEVRON_SLOT_PX = 24;
/** Child RTU indent (16px) in the RTU name cell. */
export const NAME_CHILD_INDENT_PX = 16;
/** Former / unresolved marker: 16px box + 4px margin. */
export const NAME_MARKER_PX = 20;
/** "+N" wells button: at least 32px wide, 6px gap after the well name. */
export const WELLS_BUTTON_MIN_PX = 32;
export const WELLS_BUTTON_GAP_PX = 6;
const WELLS_BUTTON_PADDING_PX = 14;
/** Expander cell: toggler (32px + 8px) and checkbox (20px + 8px) in front of a group label, then 16px per level. */
export const PRODUCER_EXTRA_PX = 72;
export const PRODUCER_LEVEL_PX = 16;

export interface TextMeasurer {
  /** Width of a cell value in the table's cell font. `sizePx` overrides the font size (the "+N" button is smaller). */
  cell: (text: string, bold?: boolean, sizePx?: number) => number;
  /** Width of a header label in the header font (bold). */
  header: (text: string) => number;
  sizePx: number;
}

/** A 2D canvas context, or null where there is none (jsdom without a canvas implementation, unless a test mocks it). */
const getMeasureContext = (): CanvasRenderingContext2D | null => {
  try {
    if (typeof document === 'undefined' || typeof HTMLCanvasElement === 'undefined') {
      return null;
    }
    const getContext = HTMLCanvasElement.prototype.getContext as unknown as object;
    const isJsdom = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent);
    if (isJsdom && !('mock' in getContext)) {
      return null; // jsdom's getContext is a "not implemented" stub that logs an error
    }
    return document.createElement('canvas').getContext('2d');
  } catch {
    return null;
  }
};

/** Canvas text measurer for one font family / size; repeated values are measured once. */
export const createTextMeasurer = (
  context: Pick<CanvasRenderingContext2D, 'measureText'> & { font: string },
  family: string,
  sizePx: number,
  headerWeight = '700'
): TextMeasurer => {
  const cache = new Map<string, number>();
  const measure = (text: string, weight: string, size: number): number => {
    const key = `${weight}|${size}|${text}`;
    let width = cache.get(key);
    if (width === undefined) {
      context.font = `${weight} ${size}px ${family}`;
      width = context.measureText(text).width;
      cache.set(key, width);
    }
    return width;
  };
  return {
    cell: (text, bold = false, size = sizePx) => measure(text, bold ? '600' : '400', size),
    header: (text) => measure(text, headerWeight, sizePx),
    sizePx,
  };
};

/**
 * Keeps, per field, the widest cell seen: text width + the fixed pieces rendered next to it + the cell padding.
 * Nothing is measured for an empty cell without extras.
 */
export class WidthAccumulator {
  private readonly widest: ContentWidths = {};
  private readonly headerWidth: ContentWidths = {};

  constructor(private readonly measurer: TextMeasurer) {}

  private bump(field: string, width: number) {
    const total = Math.ceil(width + CELL_PADDING_PX);
    if (total > (this.widest[field] ?? 0)) {
      this.widest[field] = total;
    }
  }

  header(field: string, label: string) {
    const width = this.measurer.header(label) + HEADER_EXTRA_PX;
    this.headerWidth[field] = Math.max(this.headerWidth[field] ?? 0, Math.ceil(width + CELL_PADDING_PX));
    this.bump(field, width);
  }

  value(field: string, text: string, extra = 0, bold = false) {
    if (text === '' && extra === 0) {
      return;
    }
    this.bump(field, this.measurer.cell(text, bold) + extra);
  }

  /** The widest cell per field; a field with a cap in `CONTENT_WIDTH_CAPS` is cut to it, but never below its header. */
  result(): ContentWidths {
    const result: ContentWidths = { ...this.widest };
    Object.keys(result).forEach((field) => {
      const cap = CONTENT_WIDTH_CAPS[field];
      if (cap !== undefined) {
        result[field] = Math.min(result[field], Math.max(cap, this.headerWidth[field] ?? 0));
      }
    });
    return result;
  }
}

export interface CellContent {
  text: string;
  /** Fixed pieces rendered in the same cell next to the text (chevron slot, indent, marker, "+N" button). */
  extra: number;
  bold: boolean;
}

const TIMESTAMP_FIELDS = new Set([
  'statusObservedAtUtc',
  'lastFileReceivedAtUtc',
  'lastData',
  'installDate',
  'reportingToServer',
  'lastConfigUpdate',
]);

/**
 * What a row shows in a column, as the cell renders it (the same text and the same fixed pieces); null when the column's
 * width does not depend on the row (status is a fixed dot). Used for the measuring only.
 */
export const cellContent = (row: any, field: string, clusterMode: boolean, measurer: TextMeasurer): CellContent | null => {
  switch (field) {
    case 'status':
      return null;
    case 'name': {
      const marker = row.recordState === 'FORMER' || row.recordState === 'UNRESOLVED';
      const child = row.clusterParentId !== null && row.clusterParentId !== undefined;
      return {
        text: String(row.name ?? ''),
        extra: (clusterMode ? NAME_CHEVRON_SLOT_PX : 0) + (child ? NAME_CHILD_INDENT_PX : 0) + (marker ? NAME_MARKER_PX : 0),
        bold: true, // a master's name is semibold
      };
    }
    case 'wellName': {
      const view = getWellView(row);
      const more = view.extras.length;
      const button =
        more > 0
          ? Math.max(WELLS_BUTTON_MIN_PX, measurer.cell(`+${more}`, true, measurer.sizePx * 0.9) + WELLS_BUTTON_PADDING_PX) +
            WELLS_BUTTON_GAP_PX
          : 0;
      return { text: view.shown?.name ?? '', extra: button, bold: false };
    }
    case 'serialNo':
      // shown shortened (middle ellipsis) while the cell cannot hold the full value; the full value stays available
      return { text: middleEllipsis(row.serialNo === null || row.serialNo === undefined ? '' : String(row.serialNo)), extra: 0, bold: false };
    case 'wellId':
      return { text: String(getWellView(row).shown?.wellId ?? ''), extra: 0, bold: false };
    case 'diskUsage':
    case 'cpuUsage': {
      const value = row[field];
      return { text: typeof value === 'number' ? `${value.toFixed(1)}%` : '', extra: 0, bold: false };
    }
    default: {
      const raw = row[field];
      const text = TIMESTAMP_FIELDS.has(field)
        ? formatDisplayDateTime(raw)
        : raw === null || raw === undefined
          ? ''
          : String(raw);
      return { text, extra: 0, bold: false };
    }
  }
};

/** Expander column: a group label sits behind the toggler and the checkbox, indented 16px per level. */
export const producerExtra = (level: number): number => PRODUCER_EXTRA_PX + level * PRODUCER_LEVEL_PX;

/**
 * Longest-value widths for the columns of one table. Measured in a layout effect (before paint) with the cell font read
 * from the rendered table, over the whole data set the caller passes through `compute`. Recomputed only when `deps`
 * change (the data set or the visible column set), never on filter, sort, page or scroll; and again once the web fonts
 * are loaded. Where there is no canvas the result stays empty and the floors apply.
 */
export const useContentWidths = (options: {
  enabled: boolean;
  rootRef: RefObject<HTMLElement>;
  deps: ReadonlyArray<unknown>;
  compute: (measurer: TextMeasurer) => ContentWidths;
}): ContentWidths => {
  const { enabled, rootRef, deps } = options;
  const [widths, setWidths] = useState<ContentWidths>({});
  const [fontTick, setFontTick] = useState(0);
  const computeRef = useRef(options.compute);
  computeRef.current = options.compute;

  useEffect(() => {
    const fonts = typeof document !== 'undefined' ? (document as any).fonts : undefined;
    if (fonts?.ready?.then) {
      let active = true;
      fonts.ready.then(() => active && setFontTick((tick) => tick + 1));
      return () => {
        active = false;
      };
    }
    return undefined;
  }, []);

  useLayoutEffect(() => {
    if (!enabled) {
      setWidths((previous) => (Object.keys(previous).length === 0 ? previous : {}));
      return;
    }
    const root = rootRef.current;
    const context = getMeasureContext();
    if (!root || !context) {
      return;
    }
    const cell = root.querySelector('tbody td');
    const style = getComputedStyle((cell as Element | null) ?? root);
    const fontSize = parseFloat(style.fontSize) || 12.8;
    const sizePx = cell ? fontSize : fontSize * 0.8; // table cells are 0.8rem
    const header = root.querySelector('thead th .p-column-title');
    const headerWeight = header ? getComputedStyle(header).fontWeight || '700' : '700';
    const next = computeRef.current(createTextMeasurer(context, style.fontFamily, sizePx, headerWeight));
    setWidths((previous) => {
      const keys = Object.keys(next);
      return keys.length === Object.keys(previous).length && keys.every((key) => previous[key] === next[key])
        ? previous
        : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fontTick, ...deps]);

  return widths;
};

/** Width of the table's scroll wrapper (0 until known); follows the wrapper only, through a ResizeObserver. */
export const useWrapperWidth = (rootRef: RefObject<HTMLElement>, wrapperSelector: string, enabled: boolean): number => {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const wrapper = enabled ? (rootRef.current?.querySelector(wrapperSelector) as HTMLElement | null) : null;
    if (!wrapper) {
      setWidth(0);
      return undefined;
    }
    const read = () => setWidth((previous) => (previous === wrapper.clientWidth ? previous : wrapper.clientWidth));
    read();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => read());
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [enabled, rootRef, wrapperSelector]);
  return width;
};

/** Attributes for the header cell of a column that must not be resized (selection checkbox, actions): no drag handle. */
export const NO_RESIZER_PT = { columnResizer: { style: { display: 'none' }, 'aria-hidden': true } };

/** A click that lands right after a drag ends is part of that drag, not a request to sort. */
export const RESIZE_SORT_GUARD_MS = 300;

export interface ColumnResizeEndEvent {
  element?: HTMLElement | null;
  column?: any;
  delta?: number;
}

const fieldOfResizedColumn = (event: ColumnResizeEndEvent): string | undefined => {
  const column = event.column;
  const field = column?.props?.field ?? column?.field;
  return typeof field === 'string' && field ? field : undefined;
};

/**
 * In-memory manual column widths for one table (not persisted: a reload returns to the computed widths) plus the
 * resize/sort guard.
 *  - `onColumnResizeEnd` is the PrimeReact callback: it records the dragged column's new width and starts the guard.
 *  - `shouldIgnoreSort()` is true for a short moment after a drag ends; the table's controlled `onSort` returns early.
 *  - `onMouseDownCapture` (put on a wrapper) handles a drag that PrimeReact refuses because it would take the column
 *    below its minimum (so there is no resize-end callback): the column is clamped to its minimum instead, and the
 *    release starts the guard, so that drag cannot sort either.
 * `getFields` returns the field of each header cell of the first header row, in order (undefined for the selection and
 * actions cells); `getMinWidth` the minimum of a field (floor or content width); both are only read on a drag.
 */
export const useColumnResize = (
  getFields: () => Array<string | undefined>,
  getMinWidth: (field: string) => number,
  onWidthCommitted?: () => void
) => {
  const [widths, setWidths] = useState<Record<string, number>>({});
  const guardUntil = useRef(0);
  const cleanup = useRef<(() => void) | null>(null);
  const getFieldsRef = useRef(getFields);
  getFieldsRef.current = getFields;
  const getMinRef = useRef(getMinWidth);
  getMinRef.current = getMinWidth;

  const startGuard = useCallback(() => {
    guardUntil.current = Date.now() + RESIZE_SORT_GUARD_MS;
  }, []);

  useEffect(() => () => cleanup.current?.(), []);

  const commitWidth = useCallback(
    (field: string, width: number) => {
      // Committed synchronously so the table never paints a frame between PrimeReact's DOM sizing and React's.
      flushSync(() => setWidths((previous) => ({ ...previous, [field]: width })));
      onWidthCommitted?.();
    },
    [onWidthCommitted]
  );

  const onColumnResizeEnd = useCallback(
    (event: ColumnResizeEndEvent) => {
      startGuard();
      try {
        const field = fieldOfResizedColumn(event);
        const width = event.element ? Math.round(event.element.offsetWidth) : 0;
        if (field && width > 0) {
          commitWidth(field, width);
        }
      } finally {
        // The re-render above can take a while on a big table: the guard window counts from the end of it, because
        // the browser delivers the click only after this mouseup handler returns.
        startGuard();
      }
    },
    [commitWidth, startGuard]
  );

  const onMouseDownCapture = useCallback(
    (event: ReactMouseEvent) => {
      const target = event.target as Element | null;
      const resizer = target && typeof target.closest === 'function' ? target.closest('.p-column-resizer') : null;
      if (!resizer) {
        return;
      }
      cleanup.current?.();
      const th = resizer.closest('th') as HTMLElement | null;
      const index = th?.parentElement ? Array.prototype.indexOf.call(th.parentElement.children, th) : -1;
      const field = index >= 0 ? getFieldsRef.current()[index] : undefined;
      const startX = event.clientX;
      const startWidth = th ? th.offsetWidth : 0;
      const release = (up: MouseEvent) => {
        try {
          const min = field ? getMinRef.current(field) : 0;
          if (field && startWidth > 0 && startWidth + (up.clientX - startX) < min) {
            commitWidth(field, min);
          }
        } finally {
          startGuard();
          cleanup.current?.();
        }
      };
      document.addEventListener('mouseup', release, true);
      cleanup.current = () => {
        document.removeEventListener('mouseup', release, true);
        cleanup.current = null;
      };
    },
    [commitWidth, startGuard]
  );

  /** A hidden column forgets its dragged width, so showing it again starts from the computed width. */
  const forgetWidth = useCallback((field: string) => {
    setWidths((previous) => {
      if (!(field in previous)) {
        return previous;
      }
      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const shouldIgnoreSort = useCallback(() => Date.now() < guardUntil.current, []);

  return { widths, onColumnResizeEnd, onMouseDownCapture, shouldIgnoreSort, forgetWidth };
};
